import { useState, useEffect, useCallback } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "./useAuth"

export const useUnreadMessages = () => {
  const { user } = useAuth()
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0)
      setLoading(false)
      return
    }

    try {
      // Get all conversations for the user
      const { data: conversations, error: convError } = await supabase
        .from('conversations')
        .select('id, last_message_at')
        .contains('participant_ids', [user.id])

      if (convError) throw convError

      if (!conversations || conversations.length === 0) {
        setUnreadCount(0)
        setLoading(false)
        return
      }

      // For simplicity, count conversations with messages in the last 24 hours
      // that the user hasn't seen (based on a simple heuristic)
      // In a real app, you'd track read status per message
      const recentConversations = conversations.filter(conv => {
        if (!conv.last_message_at) return false
        const lastMessageTime = new Date(conv.last_message_at)
        const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
        return lastMessageTime > dayAgo
      })

      // Check if the last message in each conversation was from someone else
      let unread = 0
      for (const conv of recentConversations) {
        const { data: lastMessage } = await supabase
          .from('messages')
          .select('sender_id')
          .eq('conversation_id', conv.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single()

        if (lastMessage && lastMessage.sender_id !== user.id) {
          unread++
        }
      }

      setUnreadCount(unread)
    } catch (error) {
      console.error('Error fetching unread messages:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchUnreadCount()

    // Set up real-time subscription for new messages
    if (!user) return

    const channel = supabase
      .channel('unread-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        () => {
          fetchUnreadCount()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, fetchUnreadCount])

  return { unreadCount, loading, refetch: fetchUnreadCount }
}
